/**
 * Shapes that match Elevar's expected Data Layer event schema:
 * https://docs.getelevar.com/docs/api-implementing-the-data-layer
 * LLM reference https://docs.getelevar.com/llms.txt
 */

// Fields that feed Elevar's user_properties. These are Elevar-only (not sent to GTM).
// All values are strings; callers serialize order/spend counts before populating UserAttributes.
// visitor_type is optional here (the source object may be partial) but required on
// ElevarUserProperties, which Elevar mandates.
export type ElevarCustomerAttributes = {
    visitor_type?: 'logged_in' | 'guest';
    customer_email?: string;
    customer_first_name?: string;
    customer_last_name?: string;
    customer_phone?: string;
    customer_address_1?: string;
    customer_address_2?: string;
    customer_city?: string;
    customer_province?: string;
    customer_province_code?: string;
    customer_zip?: string;
    customer_country?: string;
    customer_order_count?: string;
};

export type ElevarUserProperties = ElevarCustomerAttributes & {
    // required per Elevar spec (overrides the optional base)
    visitor_type: 'logged_in' | 'guest';
    customer_id?: string;
};

export type ElevarProduct = {
    id: string;
    name: string;
    brand?: string;
    category: string;
    price: string;
    compare_at_price: string;
    list: string;
    image?: string;
    quantity?: string;
    position?: string;
    url?: string;
};

export type ElevarImpression = {
    id: string;
    name: string;
    brand?: string;
    category: string;
    price: string;
    compare_at_price: string;
    position: number;
    list: string;
    quantity?: string;
};

export type ElevarClickProduct = Omit<ElevarProduct, 'position'> & {
    position: number;
};

type ElevarSubscribeUserProperties = {
    customer_email?: string;
    customer_phone?: string;
};

type WithUserProps = { user_properties: ElevarUserProperties };

type ElevarUserDataEvent = WithUserProps & {
    event: 'dl_user_data';
    cart_total: string;
    ecommerce: {
        currencyCode: string;
        cart_contents: { products: ElevarProduct[] };
    };
};

export type ElevarAuthUserProperties = Omit<
    ElevarUserProperties,
    'visitor_type' | 'customer_email'
> & {
    visitor_type: 'logged_in';
    customer_email: string;
};

type ElevarSignUpEvent = {
    event: 'dl_sign_up';
    user_properties: ElevarAuthUserProperties;
};

type ElevarLoginEvent = {
    event: 'dl_login';
    user_properties: ElevarAuthUserProperties;
};

type ElevarViewItemListEvent = WithUserProps & {
    event: 'dl_view_item_list';
    ecommerce: {
        currencyCode: string;
        impressions: ElevarImpression[];
    };
};

type ElevarViewSearchResultsEvent = WithUserProps & {
    event: 'dl_view_search_results';
    ecommerce: {
        currencyCode: string;
        actionField: { list: string };
        impressions: ElevarImpression[];
    };
};

type ElevarSelectItemEvent = WithUserProps & {
    event: 'dl_select_item';
    ecommerce: {
        currencyCode: string;
        click: {
            actionField: { list: string; action: 'click' };
            products: ElevarClickProduct[];
        };
    };
};

type ElevarViewItemEvent = WithUserProps & {
    event: 'dl_view_item';
    ecommerce: {
        currencyCode: string;
        detail: {
            actionField: { list: string; action: 'detail' };
            products: ElevarProduct[];
        };
    };
};

type ElevarAddToCartEvent = WithUserProps & {
    event: 'dl_add_to_cart';
    ecommerce: {
        currencyCode: string;
        cart_contents: { products: ElevarProduct[] };
        add: {
            actionField: { list: string };
            products: ElevarProduct[];
        };
    };
};

type ElevarRemoveFromCartEvent = WithUserProps & {
    event: 'dl_remove_from_cart';
    ecommerce: {
        currencyCode: string;
        remove: {
            actionField: { list: string };
            products: ElevarProduct[];
        };
    };
};

type ElevarViewCartEvent = WithUserProps & {
    event: 'dl_view_cart';
    cart_total: string;
    ecommerce: {
        currencyCode: string;
        actionField: Record<string, never>;
        impressions: ElevarImpression[];
    };
};

type ElevarMarketing = {
    landing_site?: string;
};

type ElevarCheckoutEcommerce = {
    currencyCode: string;
    checkout: {
        actionField: { step: string };
        products: ElevarProduct[];
    };
};

type ElevarBeginCheckoutEvent = WithUserProps & {
    event: 'dl_begin_checkout';
    marketing?: ElevarMarketing;
    ecommerce: ElevarCheckoutEcommerce;
};

type ElevarAddShippingInfoEvent = WithUserProps & {
    event: 'dl_add_shipping_info';
    marketing?: ElevarMarketing;
    ecommerce: ElevarCheckoutEcommerce;
};

type ElevarAddPaymentInfoEvent = WithUserProps & {
    event: 'dl_add_payment_info';
    marketing?: ElevarMarketing;
    ecommerce: ElevarCheckoutEcommerce;
};

export type ElevarPurchaseActionField = {
    id: string;
    order_name?: string;
    revenue?: string;
    tax?: string;
    shipping?: string;
    affiliation?: string;
    sub_total?: string;
    product_sub_total?: string;
    sales_channel?: string;
};

type ElevarPurchaseEvent = WithUserProps & {
    event: 'dl_purchase';
    marketing: ElevarMarketing;
    ecommerce: {
        currencyCode: string;
        purchase: {
            actionField: ElevarPurchaseActionField;
            products: ElevarProduct[];
        };
    };
};

type ElevarSubscribeEvent = {
    event: 'dl_subscribe';
    lead_type: 'email';
    user_properties: ElevarSubscribeUserProperties;
};

export type ElevarEvent =
    | ElevarUserDataEvent
    | ElevarSignUpEvent
    | ElevarLoginEvent
    | ElevarViewItemListEvent
    | ElevarViewSearchResultsEvent
    | ElevarSelectItemEvent
    | ElevarViewItemEvent
    | ElevarAddToCartEvent
    | ElevarRemoveFromCartEvent
    | ElevarViewCartEvent
    | ElevarBeginCheckoutEvent
    | ElevarAddShippingInfoEvent
    | ElevarAddPaymentInfoEvent
    | ElevarPurchaseEvent
    | ElevarSubscribeEvent;

export const DEFAULT_CURRENCY_CODE = 'USD';
export const ELEVAR_LIST_SEARCH_RESULTS = 'search results';
export const ELEVAR_LIST_IMAGE_SEARCH = 'image search';
export const ELEVAR_LIST_CART = 'cart';
export const ELEVAR_LIST_SAVE_FOR_LATER = 'save for later';
